/**
 * OPERIX AI ERP — Genel (auth gerektirmeyen) Gizlilik Politikası sayfası.
 *
 * URL: https://erp-fucan.com.tr/gizlilik-politikasi
 * Meta / WhatsApp Business Platform doğrulaması için erişilebilir olmalı,
 * bu yüzden robots ile bloklanmaz ve auth arkasında değildir.
 */
import { useEffect } from "react";

const LAST_UPDATED = "13 Ağustos 2026";
const CONTACT_EMAIL = "ugur@fucan.com.tr";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Gizlilik Politikası — ACUTO 360";
    // Ensure any pre-existing noindex meta is removed for this public page
    const noindex = document.querySelector('meta[name="robots"]');
    if (noindex) noindex.setAttribute("content", "index, follow");
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <header className="border-b pb-6 mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">ACUTO 360 Gizlilik Politikası</h1>
          <p className="mt-2 text-sm text-slate-500">Son güncelleme: {LAST_UPDATED}</p>
        </header>

        <section className="prose prose-slate max-w-none space-y-6 leading-relaxed text-[15px]">
          <p>
            Bu Gizlilik Politikası, <strong>Fucan Personel Giyim</strong> tarafından işletilen
            <strong> ACUTO 360</strong> ("ERP", "sistem", "biz") kapsamında toplanan kişisel verilerin
            nasıl işlendiğini ve <strong>WhatsApp Business Platform (Meta)</strong> entegrasyonu
            kapsamında bu verilerin nasıl aktarıldığını açıklar. Sistemi kullanmak, bu politikayı
            kabul ettiğiniz anlamına gelir.
          </p>

          <h2 className="text-lg font-semibold mt-8">1. Toplanan Veriler</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Ad, soyad, unvan ve iletişim bilgileri</li>
            <li>Telefon numarası (WhatsApp gönderim/alım için)</li>
            <li>WhatsApp üzerinden gönderilen ve alınan mesajlar; mesaj meta verileri (message_id, gönderim/teslim durumu, zaman damgası)</li>
            <li>ACUTO 360 kullanıcı ve müşteri bilgileri (firma bilgileri, vergi bilgileri, adres)</li>
            <li>Sipariş, teklif, üretim, sevkiyat ve fatura işlem kayıtları</li>
            <li>Sistem giriş / güvenlik günlükleri (IP, tarih, tarayıcı bilgisi)</li>
          </ul>

          <h2 className="text-lg font-semibold mt-8">2. Verilerin Kullanım Amaçları</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Müşteri iletişimi ve destek yanıtları</li>
            <li>ERP bildirimleri (sipariş durumu, üretim aşamaları, sevkiyat, kargo takibi)</li>
            <li>Personele görev atama ve süreç bildirimleri</li>
            <li>Sipariş, teklif, üretim, sevkiyat ve fatura süreçlerinin yürütülmesi</li>
            <li>Yasal yükümlülüklerin (fatura, vergi, kayıt saklama) yerine getirilmesi</li>
            <li>Sistem güvenliğinin sağlanması</li>
          </ul>

          <h2 className="text-lg font-semibold mt-8">3. WhatsApp Business Platform / Meta ile Veri Aktarımı</h2>
          <p>
            ERP, müşterilere ve personele bildirim göndermek amacıyla{" "}
            <a href="https://business.whatsapp.com/products/business-platform" className="text-emerald-700 underline" target="_blank" rel="noreferrer">
              WhatsApp Business Platform (Meta Platforms, Inc.)
            </a>{" "}
            hizmetini kullanır. Bu kapsamda alıcı telefon numarası, gönderilen mesaj içeriği,
            seçilen şablon ve alıcının onaylı gönderilen yanıtları Meta altyapısında işlenir ve iletilir.
            Meta'nın veri işleme uygulamaları için{" "}
            <a href="https://www.whatsapp.com/legal/privacy-policy" className="text-emerald-700 underline" target="_blank" rel="noreferrer">
              WhatsApp Gizlilik Politikası
            </a>{" "}
            geçerlidir.
          </p>

          <h2 className="text-lg font-semibold mt-8">4. Verilerin Üçüncü Kişilerle Paylaşımı</h2>
          <p>
            <strong>Kişisel verileriniz üçüncü kişilere pazarlama amacıyla satılmaz veya devredilmez.</strong>{" "}
            Veriler yalnızca aşağıdaki durumlarda paylaşılabilir:
          </p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Yasal zorunluluk (mahkeme kararı, resmi kurum talebi)</li>
            <li>Hizmet sağlayıcılarımız (WhatsApp/Meta, bulut altyapı, kargo firmaları) ile hizmetin gerektirdiği ölçüde</li>
            <li>Açık rızanızla yapılan aktarımlar</li>
          </ul>

          <h2 className="text-lg font-semibold mt-8">5. Güvenlik ve Saklama Süresi</h2>
          <p>
            Verileriniz TLS/HTTPS üzerinden şifreli olarak iletilir. Sunucularımıza erişim yetkili
            personelle sınırlıdır; parolalar tek yönlü şifreleme (bcrypt) ile saklanır. Yedekler
            şifrelenmiş arşivler halinde tutulur.
          </p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Aktif müşteri/işlem verileri: iş ilişkisi süresince ve <strong>10 yıla kadar</strong> (Türk Ticaret Kanunu ve Vergi Usul Kanunu gereğince).</li>
            <li>WhatsApp mesaj kayıtları: son mesaj tarihinden itibaren <strong>24 aya kadar</strong>.</li>
            <li>Sistem log kayıtları: <strong>12 aya kadar</strong>.</li>
          </ul>

          <h2 className="text-lg font-semibold mt-8">6. Haklarınız (KVKK Madde 11)</h2>
          <p>Verinizle ilgili aşağıdaki haklara sahipsiniz:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Verilerinize erişme ve kopyasını talep etme</li>
            <li>Verilerin düzeltilmesini isteme</li>
            <li>Verilerin silinmesini veya anonimleştirilmesini talep etme</li>
            <li>Otomatik karar sonuçlarına itiraz etme</li>
            <li>Verinin işlenmesine rıza vermişseniz rızanızı geri çekme</li>
          </ul>

          <h2 className="text-lg font-semibold mt-8">7. Veri Silme Talebi</h2>
          <p>
            Verilerinizin silinmesini talep etmek için{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-700 underline">
              {CONTACT_EMAIL}
            </a>{" "}
            adresine e-posta gönderebilirsiniz. Ayrıntılı talimatlar için:{" "}
            <a href="/veri-silme" className="text-emerald-700 underline">Kullanıcı Verilerini Silme Talimatları</a>.
          </p>

          <h2 className="text-lg font-semibold mt-8">8. İletişim</h2>
          <p>
            Sorularınız veya talepleriniz için:{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-emerald-700 underline">{CONTACT_EMAIL}</a>
          </p>

          <h2 className="text-lg font-semibold mt-8">9. Değişiklikler</h2>
          <p>
            Bu politikayı gerektiğinde güncelleyebiliriz. Güncel sürüm daima bu sayfada yayımlanır ve
            son güncelleme tarihi yukarıda gösterilir.
          </p>
        </section>

        <footer className="mt-12 pt-6 border-t text-xs text-slate-500 text-center">
          © {new Date().getFullYear()} Fucan Personel Giyim · ACUTO 360
        </footer>
      </div>
    </div>
  );
}
